import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';
import StatusInfo from '../components/StatusInfo';
import ListPager from '../components/ListPager';
import { VerificationBadge } from './DoctorDetail';
import { fmtMoney } from '../utils/format';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const LIMIT = 50;

export default function Doctors() {
  const [doctors, setDoctors] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc'); // asc | desc
  const [filterName, setFilterName] = useState('');
  const navigate = useNavigate();

  const key = localStorage.getItem('adminKey');

  const fetchData = useCallback((opts) => {
    const params = {
      search: filterName || undefined,
      sort: sortField, order: sortDir,
      limit: LIMIT, offset: opts?.offset ?? offset,
    };
    axios.get(`${API}/admin/doctors`, { headers: { 'X-Admin-Key': key }, params })
      .then(r => { setDoctors(r.data.items); setTotal(r.data.total); });
  }, [filterName, sortField, sortDir, offset, key]);

  // debounce на поиск/фильтры; смена сортировки/страницы — тоже через этот эффект
  useEffect(() => {
    const t = setTimeout(() => fetchData(), 250);
    return () => clearTimeout(t);
  }, [fetchData]);

  const toggleSort = (field) => {
    setOffset(0);
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const arrow = (field) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const onFilter = (setter) => (e) => { setter(e.target.value); setOffset(0); };
  const goPage = (newOffset) => setOffset(newOffset);

  return (
    <Layout>
      <h1 style={styles.title}>Врачи ({total})</h1>

      <div style={styles.table}>
        {/* Заголовки — кликабельны для сортировки */}
        <div style={styles.thead}>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('name')}>
            Врач{arrow('name')}
          </div>
          <div style={styles.th}>ID</div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('specialty')}>
            Специальность{arrow('specialty')}
          </div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('referrals_count')}>
            Направлений{arrow('referrals_count')}
          </div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('total_earned')}>
            Заработано{arrow('total_earned')}
          </div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('operations_earned')}>
            Операции{arrow('operations_earned')}
          </div>
          <div style={styles.th}>Реквизиты</div>
          <div style={styles.th}>Статус<StatusInfo def="doctor_active" /></div>
          <div style={styles.th}>Верификация<StatusInfo def="doctor_verification" /></div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('created_at')}>
            Регистрация{arrow('created_at')}
          </div>
        </div>

        {/* Строка фильтров */}
        <div style={styles.filterRow}>
          <div style={{ ...styles.filterCell, gridColumn: '1 / span 2' }}>
            <input
              style={styles.filterInput}
              placeholder="Поиск по ФИО / email / ID / специальности"
              value={filterName}
              onChange={onFilter(setFilterName)}
            />
          </div>
          <div style={styles.filterCell}></div>
          <div style={styles.filterCell}></div>
          <div style={styles.filterCell}></div>
          <div style={styles.filterCell}></div>
          <div style={styles.filterCell}></div>
          <div style={styles.filterCell}></div>
          <div style={styles.filterCell}></div>
          <div style={styles.filterCell}></div>
        </div>

        {doctors.map(d => (
          <div key={d.id} style={styles.row}>
            <div style={styles.td}>
              <div style={styles.nameLink} onClick={() => navigate(`/doctors/${d.id}`)}>{d.name}</div>
              <div style={styles.email}>{d.email}</div>
              {d.phone && <div style={styles.email}>{d.phone}</div>}
            </div>
            <div style={{ ...styles.td, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#64748b', fontSize: 12 }}>{d.public_id || '—'}</div>
            <div style={styles.td}>{d.specialty || '—'}</div>
            <div style={styles.td}>{d.referrals_count}</div>
            <div style={{ ...styles.td, color: '#2EC4B6', fontWeight: 600 }}>{fmtMoney(d.total_earned)} ₽</div>
            <div style={{ ...styles.td, color: '#f97316', fontWeight: 600 }}>{d.operations_earned > 0 ? `${fmtMoney(d.operations_earned)} ₽` : '—'}</div>
            <div style={styles.td}>
              {d.has_payout_details
                ? <span style={styles.badgeGreen}>{d.tax_status === 'self_employed' ? 'Самозанятый' : 'ИП'}</span>
                : <span style={styles.badgeGray}>Не указаны</span>}
            </div>
            <div style={styles.td}><StatusBadge d={d} /></div>
            <div style={styles.td}><VerificationBadge status={d.verification_status} /></div>
            <div style={{ ...styles.td, color: '#64748b', fontSize: 12 }}>
              {new Date(d.created_at).toLocaleDateString('ru')}
            </div>
          </div>
        ))}

        {doctors.length === 0 && (
          <div style={styles.empty}>Ничего не найдено</div>
        )}
      </div>

      <ListPager total={total} limit={LIMIT} offset={offset} onPage={goPage} />
    </Layout>
  );
}

// Единый статус-бейдж (см. StatusBadge в DoctorDetail.js — та же логика приоритета:
// ручная блокировка кабинета > анти-фрод блок > отключён > активен).
function StatusBadge({ d }) {
  if (d.is_suspended) return <span style={styles.badgeRed}>Кабинет заблокирован</span>;
  if (d.is_blocked) return <span style={styles.badgeRed}>Заблокирован (анти-фрод)</span>;
  if (!d.is_active) return <span style={styles.badgeRed}>Отключён</span>;
  return <span style={styles.badgeGreen}>Активен</span>;
}

const styles = {
  title: { color: 'white', fontSize: 24, fontWeight: 700, marginBottom: 24, marginTop: 0 },
  table: { background: '#1e293b', borderRadius: 12, overflow: 'hidden' },
  thead: { display: 'grid', gridTemplateColumns: '2fr 1fr 1.5fr 1fr 1.5fr 1.5fr 1.5fr 1fr 1fr 1fr', padding: '12px 20px', borderBottom: '1px solid #334155' },
  th: { fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  thClickable: { cursor: 'pointer', userSelect: 'none' },
  filterRow: { display: 'grid', gridTemplateColumns: '2fr 1fr 1.5fr 1fr 1.5fr 1.5fr 1.5fr 1fr 1fr 1fr', padding: '8px 20px', borderBottom: '1px solid #334155', background: '#172033', gap: 8 },
  filterCell: { display: 'flex', alignItems: 'center' },
  filterInput: {
    width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155',
    borderRadius: 6, padding: '5px 8px', fontSize: 12, outline: 'none', boxSizing: 'border-box',
  },
  row: { display: 'grid', gridTemplateColumns: '2fr 1fr 1.5fr 1fr 1.5fr 1.5fr 1.5fr 1fr 1fr 1fr', padding: '16px 20px', borderBottom: '1px solid #1e293b', background: '#0f172a', margin: '1px 0' },
  td: { fontSize: 14, color: '#e2e8f0', display: 'flex', flexDirection: 'column', justifyContent: 'center' },
  name: { fontWeight: 600, marginBottom: 2 },
  nameLink: { fontWeight: 600, marginBottom: 2, color: '#2EC4B6', cursor: 'pointer' },
  email: { fontSize: 12, color: '#64748b' },
  empty: { color: '#64748b', fontSize: 14, textAlign: 'center', padding: '40px 0' },
  badgeGreen: { background: '#052e16', color: '#4ade80', padding: '3px 8px', borderRadius: 20, fontSize: 12, display: 'inline-block' },
  badgeGray: { background: '#1e293b', color: '#64748b', padding: '3px 8px', borderRadius: 20, fontSize: 12, display: 'inline-block', border: '1px solid #334155' },
  badgeRed: { background: '#450a0a', color: '#f87171', padding: '3px 8px', borderRadius: 20, fontSize: 12, display: 'inline-block' },
};